import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import { getNewsById } from '../../services/api';
import { useLanguage } from "../../contexts/LanguageContext";
import { translateNewsItem } from "../../utils/translationHelper";
import DOMPurify from "dompurify";

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

function NewsDetail() {
    const { id } = useParams();
    const [newsItem, setNewsItem] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const { currentLanguage } = useLanguage();

    // Месяцы на разных языках
    const months = {
        kz: [
            'ҚАҢТАР', 'АҚПАН', 'НАУРЫЗ', 'СӘУІР', 'МАМЫР', 'МАУСЫМ',
            'ШІЛДЕ', 'ТАМЫЗ', 'ҚЫРКҮЙЕК', 'ҚАЗАН', 'ҚАРАША', 'ЖЕЛТОҚСАН'
        ],
        ru: [
            'ЯНВАРЬ', 'ФЕВРАЛЬ', 'МАРТ', 'АПРЕЛЬ', 'МАЙ', 'ИЮНЬ',
            'ИЮЛЬ', 'АВГУСТ', 'СЕНТЯБРЬ', 'ОКТЯБРЬ', 'НОЯБРЬ', 'ДЕКАБРЬ'
        ],
        en: [
            'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
            'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'
        ]
    };

    // Переводы текстов
    const translations = {
        kz: {
            loading: 'Жүктелуде...',
            notFound: 'Жаңалық табылмады',
            error: 'Қате орын алды',
        },
        ru: {
            loading: 'Загрузка...',
            notFound: 'Новость не найдена',
            error: 'Произошла ошибка',
        },
        en: {
            loading: 'Loading...',
            notFound: 'News not found',
            error: 'An error occurred',
        },
    };

    const t = translations[currentLanguage] || translations.kz;
    const currentMonths = months[currentLanguage] || months.kz;

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const day = date.getDate();
        const month = currentMonths[date.getMonth()];
        const year = date.getFullYear();
        return `${day} ${month}, ${year}`;
    };

    useEffect(() => {
        const loadNewsItem = async () => {
            try {
                setLoading(true);

                const res = await getNewsById(id);
                if (res.news) {

                    const translatedNews = translateNewsItem(res.news, currentLanguage);
                    setNewsItem(translatedNews);
                    setError(null);
                } else {
                    setError(t.notFound);
                }
            } catch (err) {
                console.error('Error loading news:', err);
                setError(t.error);
            } finally {
                setLoading(false);
            }
        };

        loadNewsItem();
    }, [id, currentLanguage, t.notFound, t.error]);

    if (loading) return <div className="detail-loading">{t.loading}</div>;
    if (error || !newsItem) return <div className="detail-not-found">{error}</div>;

    const images = newsItem.images && newsItem.images.length > 0
        ? newsItem.images
        : newsItem.img_url ? [{ img_url: newsItem.img_url }] : [];

    return (
        <div className="detail-container">
            <div className="detail-header">
                <div className="detail-slider-container">
                    {images.length > 1 ? (
                        <Swiper
                            modules={[Navigation, Pagination, Autoplay]}
                            spaceBetween={0}
                            slidesPerView={1}
                            navigation
                            pagination={{ clickable: true }}
                            autoplay={{ delay: 3000, disableOnInteraction: false }}
                            className="detail-swiper"
                        >
                            {images.map((img, index) => (
                                <SwiperSlide key={img.id || index}>
                                    <img
                                        className="detail-image"
                                        src={`${process.env.REACT_APP_API_URL}${img.img_url}`}
                                        alt={`${newsItem.title} - ${index + 1}`}
                                    />
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    ) : (
                        images.length > 0 && (
                            <img
                                className="detail-image"
                                src={`${process.env.REACT_APP_API_URL}${images[0].img_url}`}
                                alt={newsItem.title || t.notFound}
                            />
                        )
                    )}
                </div>
                <div className="detail-info">
                    <h1 className="detail-title">{newsItem.title}</h1>
                    <p className="detail-date">{formatDate(newsItem.date)}</p>
                    <div className="detail-social">
                        {newsItem.instagram &&
                          <a href={newsItem.instagram} target="_blank" rel="noopener noreferrer"><i className="fab fa-instagram"></i></a>
                        }
                    </div>
                </div>
            </div>
            <div className="detail-description">
                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(newsItem.description).replace(/\n/g, '<br />') }} />
            </div>
        </div>
    );
}

export default NewsDetail;